import React from "react";

import { readStrategyFields, classifyStrategy } from "./PaperStrategyCard";
import { verdictLabelOf } from "../../data/verdict";

/**
 * StatusRibbon — 전략 상태를 "설명하는 정적 범례" 대신 "살아있는 분포 바"로 보여준다.
 *
 * 두 줄:
 *   운영 상태  진입중 / 활성 / 휴면 / 신호 대기   (지금 무엇을 하나)
 *   자동 판정  GO / 검증중 / NO-GO / 데이터 부족   (성과가 어떤가)
 *
 * 각 칸은 (1) 현재 개수를 폭으로 표현 → "지금 다 정상인가?"가 한눈에,
 * (2) hover/focus 하면 그 상태의 의미를 설명,
 * (3) 운영 상태는 클릭하면 해당 상태로 필터(actionable 범례).
 *
 * 색·토큰은 카드의 배지(StatusBadge/VerdictBadge)와 1:1 로 맞춰 인지 일관성을 둔다.
 * DESIGN_SYSTEM 시맨틱 토큰만 사용(3 테마·WCAG 보존).
 */

const OPS = [
    {
        key: "active",
        label: "진입중",
        seg: "bg-brand",
        text: "text-brand-dark",
        dot: "bg-brand",
        pulse: true,
        desc: "포지션을 보유하고 매매가 진행 중. 가장 활발한 상태.",
    },
    {
        key: "live",
        label: "활성",
        seg: "bg-gain",
        text: "text-gain",
        dot: "bg-gain",
        desc: "최근 24시간 안에 거래 발생. 신호가 정상적으로 나오는 중.",
    },
    {
        key: "idle",
        label: "휴면",
        seg: "bg-warning",
        text: "text-warning",
        dot: "bg-warning",
        desc: "가동 중이나 24시간+ 거래 없음. 신호 조건이 한동안 안 맞은 상태(고장 아님).",
    },
    {
        key: "waiting",
        label: "신호 대기",
        seg: "bg-content-muted/50",
        text: "text-content-secondary",
        dot: "bg-content-muted",
        desc: "아직 한 번도 진입 안 함. 진입 조건 충족 대기 — 조건이 까다로운 전략은 며칠 대기가 정상.",
    },
];

const VERDICTS = [
    {
        key: "go",
        label: "GO",
        seg: "bg-gain",
        text: "text-gain",
        glyph: "●",
        desc: "거래≥30 · PF≥1.3 · 승률≥40% · 순익(+) · 2주+ — 실전 투입 후보.",
    },
    {
        key: "continue",
        label: "검증중",
        seg: "bg-brand",
        text: "text-brand-dark",
        glyph: "◐",
        desc: "표본 부족/검증 진행 중. 계속 관찰(아직 합격·탈락 아님).",
    },
    {
        key: "nogo",
        label: "NO-GO",
        seg: "bg-loss",
        text: "text-loss",
        glyph: "✕",
        desc: "거래 10건+ 인데 PF<1 또는 승률<30% 또는 누적 손실 — 폐기 후보.",
    },
    {
        key: "dormant",
        label: "데이터 부족",
        seg: "bg-content-muted/40",
        text: "text-content-secondary",
        glyph: "◌",
        desc: "거래 5건 미만 — 성과 판단 보류. 대부분 신규/희소 전략.",
    },
];

function tally(strategies, classify) {
    const counts = {};
    for (const s of strategies) counts[classify(s)] = (counts[classify(s)] || 0) + 1;
    return counts;
}

/** 분포 바 1줄 + 칩 — segments 정의와 counts 로 렌더. */
function DistRow({ title, segments, counts, total, activeKey, onPick }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-semibold text-content-secondary uppercase tracking-wide w-12 shrink-0">
                    {title}
                </span>
                {/* 비율 바 */}
                <div className="flex-1 flex h-2.5 rounded-full overflow-hidden bg-surface-elevated">
                    {segments.map((seg) => {
                        const n = counts[seg.key] || 0;
                        if (n === 0) return null;
                        const pct = total > 0 ? (n / total) * 100 : 0;
                        return (
                            <div
                                key={seg.key}
                                className={`${seg.seg} h-full transition-[width] duration-700 ease-out`}
                                style={{ width: `${pct}%`, minWidth: n > 0 ? "3px" : 0 }}
                                title={`${seg.label} ${n}`}
                            />
                        );
                    })}
                </div>
            </div>
            {/* 칩 (개수 + hover 설명 + 클릭 필터) */}
            <div className="flex flex-wrap gap-1.5 pl-12">
                {segments.map((seg) => {
                    const n = counts[seg.key] || 0;
                    const isActive = activeKey === seg.key;
                    const clickable = !!onPick;
                    const Tag = clickable ? "button" : "div";
                    return (
                        <Tag
                            key={seg.key}
                            type={clickable ? "button" : undefined}
                            onClick={clickable ? () => onPick(isActive ? "all" : seg.key) : undefined}
                            aria-pressed={clickable ? isActive : undefined}
                            className={`group relative inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors ${
                                isActive
                                    ? "bg-surface-elevated ring-1 ring-brand/40"
                                    : clickable
                                      ? "hover:bg-surface-hover"
                                      : ""
                            }`}
                        >
                            {/* 가시 콘텐츠만 dimming — opacity 를 Tag(=툴팁 조상)에 주면
                                자식 설명 팝오버까지 반투명해지므로(카운트 0 칩) 분리한다. */}
                            <span
                                className={`inline-flex items-center gap-1.5 ${
                                    n === 0 ? "opacity-40" : ""
                                }`}
                            >
                                <span
                                    aria-hidden="true"
                                    className={`inline-block h-2 w-2 rounded-full ${seg.dot ?? ""} ${
                                        seg.glyph ? "hidden" : ""
                                    } ${seg.pulse && n > 0 ? "animate-pulse" : ""}`}
                                />
                                {seg.glyph && (
                                    <span aria-hidden="true" className={`text-xs leading-none ${seg.text}`}>
                                        {seg.glyph}
                                    </span>
                                )}
                                <span className="text-content font-medium">{seg.label}</span>
                                <span className={`tabular-nums font-semibold ${seg.text}`}>{n}</span>
                            </span>
                            {/* hover/focus 설명 팝오버 — Tag 직속이라 칩 dimming 영향 없음 */}
                            <span
                                role="tooltip"
                                className="pointer-events-none absolute left-0 top-full mt-1.5 z-30 w-60 rounded-lg border border-surface-border bg-surface-card p-2.5 text-left text-xs leading-snug text-content-secondary opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
                            >
                                <span className="block font-semibold text-content mb-0.5">
                                    {seg.label}
                                </span>
                                {seg.desc}
                                {clickable && (
                                    <span className="block mt-1 text-content-muted">
                                        클릭하면 이 상태만 보기
                                    </span>
                                )}
                            </span>
                        </Tag>
                    );
                })}
            </div>
        </div>
    );
}

export default function StatusRibbon({
    strategies,
    filterStatus = "all",
    onFilterStatus,
    filterVerdict = "all",
    onFilterVerdict,
    familyVerdictMap = null,
}) {
    const list = Array.isArray(strategies) ? strategies : [];
    const total = list.length;

    const opsCounts = tally(list, (s) => {
        const f = readStrategyFields(s);
        return classifyStrategy({
            openCount: f.openCount,
            lastTradeTs: f.lastTradeTs,
            trades: f.trades,
        });
    });
    // 판정 카운트는 페이지 필터(filterVerdict)와 동일한 소스를 써야 칩 숫자와
    // 클릭 필터 결과가 일치한다 — 그룹은 백엔드 family verdict 우선(verdictLabelOf).
    const verdictCounts = tally(list, (s) =>
        verdictLabelOf(s, familyVerdictMap, readStrategyFields(s)),
    );

    const tradingNow = (opsCounts.active || 0) + (opsCounts.live || 0);

    return (
        <div className="bg-surface-card border border-surface-border rounded-lg p-3 mb-3 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-sm font-medium text-content flex items-center gap-2">
                    전략 상태 현황
                    <span className="text-xs text-content-muted font-normal">
                        칸을 탭·클릭하면 의미·필터
                    </span>
                </h3>
                <span className="text-xs text-content-secondary tabular-nums">
                    총 {total} · 거래 활성{" "}
                    <span className={tradingNow > 0 ? "text-gain font-semibold" : "text-content-muted"}>
                        {tradingNow}
                    </span>
                </span>
            </div>
            <DistRow
                title="운영"
                segments={OPS}
                counts={opsCounts}
                total={total}
                activeKey={filterStatus}
                onPick={onFilterStatus}
            />
            <DistRow
                title="판정"
                segments={VERDICTS}
                counts={verdictCounts}
                total={total}
                activeKey={filterVerdict}
                onPick={onFilterVerdict}
            />
        </div>
    );
}
